/**
 * wechat-jssdk.provider.tsx
 *
 * Context provider for managing WeChat JSSDK initialization state globally.
 * This ensures only one SDK instance is created and shared across all components.
 *
 * Prerequisites:
 * - WeChat public account configured
 * - Backend API endpoint for signature generation
 * - wechat-jssdk package installed
 *
 * @see https://github.com/JasonBoy/wechat-jssdk
 */

import React, { useCallback, useRef, useState } from 'react'
import { Platform } from 'react-native'
import { useWechatSignatureMutation } from '../generated/graphql'
import {
  initializeWechatJSSDK,
  WechatJSSDKInstance,
} from '../hooks/wechat-jssdk-initialize.hook'
import * as Sentry from '@sentry/react-native'

interface WechatJSSDKContextValue {
  wxInstance: WechatJSSDKInstance | null
  isReady: boolean
  isInitializing: boolean
  error: Error | null
  initialize: (
    url: string,
    jsApiList: Array<string>
  ) => Promise<WechatJSSDKInstance | null>
}

const WechatJSSDKContext = React.createContext<WechatJSSDKContextValue | null>(
  null
)

interface WechatJSSDKProviderProps {
  children: React.ReactNode
}

export function WechatJSSDKProvider(props: WechatJSSDKProviderProps) {
  const [doSignature] = useWechatSignatureMutation()
  const [wxInstance, setWxInstance] = useState<WechatJSSDKInstance | null>(null)
  const [isInitializing, setIsInitializing] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const initAttemptedRef = useRef(false)

  const initialize = useCallback(
    async (url: string, jsApiList: Array<string>) => {
      if (Platform.OS !== 'web') {
        return null
      }

      if (initAttemptedRef.current || isInitializing || wxInstance) {
        return wxInstance
      }

      initAttemptedRef.current = true
      setIsInitializing(true)

      try {
        const { instance } = await initializeWechatJSSDK({
          sign: doSignature,
          url,
          jsApiList,
        })

        if (instance) {
          setWxInstance(instance)
          setError(null)
          return instance
        }
        return null
      } catch (err) {
        const errorInstance =
          err instanceof Error ? err : new Error(String(err))
        setError(errorInstance)
        const errorMessage = errorInstance.message
        console.error(
          `[WechatJSSDKProvider] Initialization error: ${errorMessage}`
        )
        Sentry.captureException(err, {
          tags: {
            component: 'WechatJSSDKProvider',
            operation: 'initialize',
          },
        })
        initAttemptedRef.current = false
        return null
      } finally {
        setIsInitializing(false)
      }
    },
    [doSignature, isInitializing, wxInstance]
  )

  const isReady = wxInstance !== null

  const value: WechatJSSDKContextValue = {
    wxInstance,
    isReady,
    isInitializing,
    error,
    initialize,
  }

  return (
    <WechatJSSDKContext.Provider value={value}>
      {props.children}
    </WechatJSSDKContext.Provider>
  )
}

/**
 * Hook to access the WeChat JSSDK context.
 * This hook provides access to the shared WeChat JSSDK instance and initialization state.
 *
 * @returns Object containing:
 *   - wxInstance: The initialized WeChat JSSDK instance, or null if not initialized
 *   - isReady: Boolean indicating if the SDK is ready to use
 *   - isInitializing: Boolean indicating if initialization is in progress
 *   - error: Error object if initialization failed, or null if successful/not attempted
 *   - initialize: Function to manually trigger initialization
 *
 * @throws Error if used outside WechatJSSDKProvider
 *
 * @example
 * // In your component:
 * function MyComponent() {
 *   const { wxInstance, isReady, initialize } = useWechatJSSDK();
 *
 *   useEffect(() => {
 *     if (Platform.OS === 'web' && isWeChatBrowser()) {
 *       initialize(location.href, ['updateAppMessageShareData']);
 *     }
 *   }, [initialize]);
 *
 *   // Use wxInstance when ready
 *   if (isReady && wxInstance) {
 *     // Call WeChat APIs
 *   }
 * }
 */
export function useWechatJSSDK(): WechatJSSDKContextValue {
  const context = React.useContext(WechatJSSDKContext)
  if (!context) {
    throw new Error('useWechatJSSDK must be used within a WechatJSSDKProvider')
  }
  return context
}
